import config from '../config';

let isInitialized = false;

/**
 * Checks whether debug mode should be enabled for Clarity logs.
 */
function isDebug() {
  if (typeof window === 'undefined') return false;
  return (
    config.CLARITY_DEBUG_MODE === true ||
    process.env.NODE_ENV !== 'production' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.search.includes('clarity_debug=true')
  );
}

/**
 * Log helper with styling.
 */
function logDebug(message, ...args) {
  if (isDebug()) {
    console.log(
      `%c[Clarity]%c ${message}`,
      'color:#0078D4;font-weight:bold',
      'color:inherit',
      ...args
    );
  }
}

/**
 * Initializes Microsoft Clarity once with the configured Project ID.
 * Loads the official Clarity SDK script asynchronously and sets up window.clarity queue.
 * Gracefully handles missing project ID without throwing errors.
 */
export function initClarity() {
  if (typeof window === 'undefined') return;

  // Singleton guard: prevent duplicate initialization
  if (isInitialized || window.clarity?.v) {
    logDebug('Already initialized. Skipping duplicate init.');
    return;
  }

  const projectId = config.CLARITY_PROJECT_ID;

  if (!projectId || typeof projectId !== 'string' || !projectId.trim()) {
    logDebug('No CLARITY_PROJECT_ID found in config.js. Tracking remains inactive.');
    return;
  }

  const cleanProjectId = projectId.trim();

  // Prevent duplicate script tag injection
  if (document.getElementById('clarity-tracking-script')) {
    return;
  }

  try {
    // Official Microsoft Clarity loader snippet:
    // Sets up queue function on window.clarity and asynchronously injects tag script
    (function (c, l, a, r, i, t, y) {
      c[a] =
        c[a] ||
        function () {
          (c[a].q = c[a].q || []).push(arguments);
        };
      t = l.createElement(r);
      t.id = 'clarity-tracking-script';
      t.async = 1;
      t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0];
      if (y && y.parentNode) {
        y.parentNode.insertBefore(t, y);
      } else {
        l.head.appendChild(t);
      }
    })(window, document, 'clarity', 'script', cleanProjectId);

    isInitialized = true;
    logDebug(`Initialized successfully with Project ID: %c${cleanProjectId}`, 'color:#107C41;font-weight:bold');
  } catch (err) {
    console.warn('[Clarity] Initialization error:', err);
  }
}

/**
 * Identifies the current authenticated user using an internal, non-sensitive identifier.
 * STRICT PRIVACY: Validates that no emails, tokens, or PII are passed.
 *
 * @param {string|number} userId Internal database/account identifier (e.g. 1234 or uuid)
 */
export function identifyUser(userId) {
  if (typeof window === 'undefined' || !userId) return;

  const idStr = String(userId).trim();
  if (!idStr) return;

  // Privacy Safeguard: Reject email addresses or tokens passed as user ID
  if (idStr.includes('@') || idStr.length > 100) {
    console.warn('[Clarity] identifyUser rejected potential PII/token:', idStr);
    return;
  }

  try {
    if (typeof window.clarity === 'function') {
      window.clarity('identify', idStr);
      logDebug(`Identified user with internal ID: ${idStr}`);
    }
  } catch (err) {
    console.warn('[Clarity] Error identifying user:', err);
  }
}

/**
 * Clears user identification upon logout.
 */
export function clearClarityUser() {
  if (typeof window === 'undefined') return;

  try {
    if (typeof window.clarity === 'function') {
      // Calling identify with empty / anonymous ID resets the user identifier in Clarity
      window.clarity('identify', '');
      logDebug('Cleared user identification.');
    }
  } catch (err) {
    console.warn('[Clarity] Error clearing user:', err);
  }
}

/**
 * Sensitive parameter keys that must NEVER be passed to Clarity tags.
 */
const SENSITIVE_KEYS = new Set([
  'password',
  'passwd',
  'token',
  'accesstoken',
  'jwt',
  'email',
  'card',
  'cvv',
  'cvc',
  'cardnumber',
  'exp_month',
  'exp_year',
  'accountbalance',
  'balance',
  'secret',
  'auth',
]);

/**
 * Tracks a custom event in Microsoft Clarity.
 *
 * @param {string} eventName Name of custom event (e.g. 'user_login', 'dashboard_opened')
 * @param {object} [params={}] Safe non-sensitive metadata to attach as tags
 */
export function trackClarityEvent(eventName, params = {}) {
  if (typeof window === 'undefined' || !eventName) return;

  try {
    if (typeof window.clarity === 'function') {
      // 1. Dispatch custom event
      window.clarity('event', eventName);

      // 2. Attach safe non-sensitive parameters as Clarity custom tags
      if (params && typeof params === 'object') {
        Object.entries(params).forEach(([key, val]) => {
          if (val === null || val === undefined) return;
          const lowerKey = key.toLowerCase();
          if (SENSITIVE_KEYS.has(lowerKey)) return;

          // Extra safety: ensure value does not look like an email or JWT
          const strVal = String(val).trim();
          if (strVal.includes('@') || strVal.startsWith('eyJ')) return;

          window.clarity('set', key, strVal);
        });
      }

      logDebug(`Event tracked: %c${eventName}`, 'color:#0078D4;font-weight:bold', params);
    } else {
      logDebug(`Event dry-run (clarity not active): ${eventName}`, params);
    }
  } catch (err) {
    console.warn(`[Clarity] Error tracking event ${eventName}:`, err);
  }
}

/**
 * Informs Clarity of virtual page navigation in SPA (Vue Router hash mode).
 *
 * @param {string} pageName Virtual page name or route path
 */
export function setClarityPage(pageName) {
  if (typeof window === 'undefined' || !pageName) return;

  try {
    if (typeof window.clarity === 'function') {
      window.clarity('set', 'page', String(pageName));
      logDebug(`Page set to: ${pageName}`);
    }
  } catch (err) {
    console.warn('[Clarity] Error setting page:', err);
  }
}

// Global console helper for testing
if (typeof window !== 'undefined') {
  window.ptClarityTest = function (eventName = 'test_event', params = {}) {
    console.log('[Clarity] Manually firing test event...');
    trackClarityEvent(eventName, params);
  };
}
